import {
	ClientInsuredPersonRiskVariantEnum,
	ClientPortfolioInsuredPersonRiskEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { normalizeBirthNumber } from "web_myplann/Shared/Utils/birthNumber";
import { requiredBirthNumber } from "web_myplann/Shared/Validation/requiredBirthNumber";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

const NAME_MAX_LENGTH = 255;

const getInsuredRiskSchema = (t: TranslateFunction) => {
	const required = t(Resources.Portfolio.AddShared.Validations.required);

	return z.object({
		id: z.number().nullable(),
		risk: z
			.nativeEnum(ClientPortfolioInsuredPersonRiskEnum)
			.nullable()
			.refine((risk) => risk !== null, required),
		value: z
			.number({ invalid_type_error: required })
			.gt(0, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
			.max(
				ValidationLimits.Amount.Max,
				t(Resources.Portfolio.AddShared.Validations.maxValue),
			)
			.nullable()
			.refine((value) => value !== null, required),
		toAge: z
			.number()
			.int(t(Resources.Portfolio.AddShared.Validations.positiveNumber))
			.min(1, t(Resources.Portfolio.AddShared.Validations.positiveNumber))
			.max(
				ValidationLimits.Age.Max,
				t(Resources.Portfolio.AddShared.Validations.maxValue),
			)
			.nullish()
			.transform((toAge) => toAge ?? null),
		variant: z.nativeEnum(ClientInsuredPersonRiskVariantEnum).nullable(),
		additionalInfo: z.string().nullable(),
	});
};

const getRequiredName = (t: TranslateFunction) =>
	z
		.string()
		.trim()
		.min(1, t(Resources.Portfolio.AddShared.Validations.required))
		.max(
			NAME_MAX_LENGTH,
			t(Resources.Portfolio.AddShared.Validations.maxLength, {
				max: NAME_MAX_LENGTH,
			}),
		);

export const getInsuredPersonSchema = (
	t: TranslateFunction,
	takenIdentificationNumbers: string[] = [],
) =>
	z.object({
		id: z.number().nullable(),
		firstname: getRequiredName(t),
		lastname: getRequiredName(t),
		identificationNumber: requiredBirthNumber(
			t(Resources.Portfolio.AddShared.Validations.required),
			t(Resources.Portfolio.AddShared.Validations.birthNumberFormat),
		).refine(
			(value) =>
				!takenIdentificationNumbers.includes(normalizeBirthNumber(value)),
			t(
				Resources.Portfolio.AddLifeInsurance.Validations
					.duplicateIdentificationNumber,
			),
		),
		risks: z.array(getInsuredRiskSchema(t)),
	});

export type InsuredPersonInput = z.input<
	ReturnType<typeof getInsuredPersonSchema>
>;
export type InsuredPersonOutput = z.output<
	ReturnType<typeof getInsuredPersonSchema>
>;
export type InsuredRiskInput = InsuredPersonInput["risks"][number];

export const INSURED_PERSON_DEFAULT_VALUES: InsuredPersonInput = {
	id: null,
	firstname: "",
	lastname: "",
	identificationNumber: "",
	risks: [],
};

export const INSURED_RISK_DEFAULT_VALUES: InsuredRiskInput = {
	id: null,
	risk: null,
	value: null,
	toAge: null,
	variant: null,
	additionalInfo: null,
};
